import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { pickAndUploadImage } from "../lib/upload";
import { useAuth } from "../context/AuthContext";
import AdminJobs from "./AdminJobs";
import AdminClients from "./admin/AdminClients";
import AdminTeam from "./admin/AdminTeam";
import AdminProfile from "./admin/AdminProfile";
import AdminOverview from "./admin/AdminOverview";
import AdminContent from "./admin/AdminContent";
import {
  LayoutDashboard, FolderKanban, Users, UserCog, Image as ImageIcon,
  Inbox, User, LogOut, Trash2, Upload, Menu, X, Paintbrush, Loader2,
} from "lucide-react";

const NAV = [
  { k: "overview", l: "Dashboard", icon: LayoutDashboard },
  { k: "projects", l: "Projects", icon: FolderKanban },
  { k: "clients", l: "Clients", icon: Users },
  { k: "team", l: "Team", icon: UserCog },
  { k: "content", l: "Site Content", icon: Paintbrush },
  { k: "gallery", l: "Before/After", icon: ImageIcon },
  { k: "leads", l: "Leads", icon: Inbox },
  { k: "profile", l: "Profile", icon: User },
];

/* Marketing Before/After gallery manager */
const CATEGORIES = ["Kitchen", "Bathroom", "Basement", "Full Home", "Exterior"];

function BAImageSlot({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const url = await pickAndUploadImage(f);
      onChange(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <label data-testid={testid} className={`border border-white/15 aspect-video overflow-hidden relative cursor-pointer hover:border-gold group ${uploading ? "border-gold cursor-wait" : ""}`}>
      {value ? (
        <img src={value} alt={label} className="h-full w-full object-cover" />
      ) : (
        <span className="h-full flex flex-col items-center justify-center gap-1 text-xs text-white/40 uppercase tracking-widest group-hover:text-gold">
          {uploading ? <Loader2 size={16} className="animate-spin text-gold" /> : <Upload size={16} />}
          {uploading ? "Uploading…" : label}
        </span>
      )}
      <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={handle} />
    </label>
  );
}

function GalleryManager() {
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ title: "", category: "Kitchen", location: "", description: "" });
  const [before, setBefore] = useState(""); const [after, setAfter] = useState(""); const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); if (!before || !after) return toast.error("Upload both images");
    if (!form.title.trim()) return toast.error("Title required");
    setSaving(true);
    try {
      await api.post("/admin/projects", { ...form, before_image: before, after_image: after });
      toast.success("Published to Before/After");
      setForm({ title: "", category: "Kitchen", location: "", description: "" });
      setBefore(""); setAfter("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error saving");
    } finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this before/after project?")) return;
    await api.delete(`/admin/projects/${id}`);
    toast.success("Deleted");
    load();
  };
  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-2">
        <h2 className="font-serif text-3xl md:text-4xl">Before / After Showcase</h2>
        <p className="text-white/40 text-xs">{projects.length} project{projects.length !== 1 && "s"} · newest 10 shown publicly</p>
      </div>
      <p className="text-white/40 text-sm mb-8">Upload the latest projects — they appear on the homepage section and on the public <span className="text-gold">/before-after</span> page.</p>
      <div className="grid lg:grid-cols-[360px_1fr] gap-8">
        <form onSubmit={submit} className="border border-white/10 p-6 space-y-4 h-fit bg-[#0a0a0a]" data-testid="ba-form">
          <h3 className="font-serif text-xl">Add new</h3>
          <input required placeholder="Project title" value={form.title} onChange={set("title")} className="editorial-input" data-testid="ba-title" />
          <select value={form.category} onChange={set("category")} className="editorial-input [&>option]:bg-black" data-testid="ba-category">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
          <input placeholder="Location (e.g. Fairfax, VA)" value={form.location} onChange={set("location")} className="editorial-input" data-testid="ba-location" />
          <textarea rows={2} placeholder="Short description (optional)" value={form.description} onChange={set("description")} className="editorial-input resize-none" data-testid="ba-desc" />
          <p className="text-[10px] uppercase tracking-widest text-white/50 pt-2">Photos — tap to upload from device</p>
          <div className="grid grid-cols-2 gap-3">
            <BAImageSlot label="Before" value={before} onChange={setBefore} testid="ba-before-slot" />
            <BAImageSlot label="After" value={after} onChange={setAfter} testid="ba-after-slot" />
          </div>
          <button disabled={saving} data-testid="ba-submit" className="w-full bg-gold text-black text-xs uppercase tracking-widest py-3 hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50"><Upload size={15} /> {saving ? "Saving…" : "Publish"}</button>
        </form>
        <div className="grid sm:grid-cols-2 gap-4">
          {projects.map((p, i) => (
            <div key={p.id} data-testid={`ba-item-${p.id}`} className="border border-white/10 overflow-hidden bg-[#0a0a0a]">
              <div className="grid grid-cols-2 relative">
                <img src={p.before_image} alt="before" className="aspect-square object-cover" />
                <img src={p.after_image} alt="after" className="aspect-square object-cover" />
                {i < 10 && <span className="absolute top-2 left-2 bg-gold text-black text-[10px] uppercase tracking-widest px-2 py-0.5 font-semibold">Public</span>}
              </div>
              <div className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-serif text-lg">{p.title}</p>
                  <p className="text-white/40 text-xs">{p.category} · {p.location || "—"}</p>
                </div>
                <button onClick={() => del(p.id)} data-testid={`ba-delete-${p.id}`} className="text-white/40 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
          {!projects.length && <p className="text-white/40">No showcase items yet. Add your first project on the left.</p>}
        </div>
      </div>
    </div>
  );
}

function LeadsManager() {
  const [leads, setLeads] = useState([]);
  useEffect(() => { api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h2 className="font-serif text-3xl md:text-4xl mb-8">Quote Requests</h2>
      <div className="space-y-3">
        {leads.map((l) => (
          <div key={l.id} className="border border-white/10 p-5 grid md:grid-cols-[1fr_auto] gap-4">
            <div><p className="text-white">{l.name} · <span className="text-gold">{l.service || "General"}</span></p><p className="text-white/50 text-sm">{l.email} · {l.phone}</p>{l.message && <p className="text-white/60 text-sm mt-2">{l.message}</p>}</div>
            <p className="text-white/30 text-xs">{new Date(l.created_at).toLocaleString()}</p>
          </div>
        ))}
        {!leads.length && <p className="text-white/40">No requests yet.</p>}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("overview");
  const [open, setOpen] = useState(false);
  const go = (k) => { setSection(k); setOpen(false); };
  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className={`lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-white/10 lg:sticky lg:top-0`}>
        <div className="flex items-center justify-between px-6 h-20">
          <span className="font-serif text-xl"><span className="gold-gradient-text">GI</span> Admin</span>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
        </div>
        <nav className={`${open ? "block" : "hidden"} lg:block px-3 pb-4`}>
          {NAV.map((n) => { const Icon = n.icon; const active = section === n.k; return (
            <button key={n.k} data-testid={`admin-nav-${n.k}`} onClick={() => go(n.k)} className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${active ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-white/60 hover:text-white border-l-2 border-transparent"}`}><Icon size={17} /> {n.l}</button>
          ); })}
          <button data-testid="admin-logout" onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/50 hover:text-red-400 mt-4"><LogOut size={16} /> Log out</button>
          <p className="px-4 mt-6 text-xs text-white/30">{user?.name} · {user?.role}</p>
        </nav>
      </aside>

      {/* Content */}
      <main className="flex-1 p-5 md:p-10 max-w-[1200px]">
        {section === "overview" && <AdminOverview onGoProjects={() => setSection("projects")} />}
        {section === "projects" && <AdminJobs />}
        {section === "clients" && <AdminClients />}
        {section === "team" && <AdminTeam />}
        {section === "content" && <AdminContent />}
        {section === "gallery" && <GalleryManager />}
        {section === "leads" && <LeadsManager />}
        {section === "profile" && <AdminProfile />}
      </main>
    </div>
  );
}
